"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, Mail, Phone, MapPin } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="text-center py-8">
        <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-display font-bold text-foreground-light mb-2">
          Message Sent!
        </h3>
        <p className="text-accent mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="hover:-translate-y-0.5 transition-transform duration-[80ms]">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <div>
          <h3 className="text-2xl font-display font-bold text-foreground-light mb-2">
            Send Us a Message
          </h3>
          <p className="text-accent text-sm">
            Fill out the form below and we'll get back to you promptly.
          </p>
        </div>

        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="border-b-2 border-muted focus-visible:border-primary transition-all duration-[120ms]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@example.com" {...field} className="border-b-2 border-muted focus-visible:border-primary transition-all duration-[120ms]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number (Optional)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+971 XX XXX XXXX" {...field} className="border-b-2 border-muted focus-visible:border-primary transition-all duration-[120ms]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" rules={{
        required: "Subject is required",
        minLength: {
          value: 3,
          message: "Subject must be at least 3 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Subject</FormLabel>
              <FormControl>
                <Input placeholder="How can we help you?" {...field} className="border-b-2 border-muted focus-visible:border-primary transition-all duration-[120ms]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Message</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us more about your inquiry..." className="min-h-32 border-b-2 border-muted focus-visible:border-primary transition-all duration-[120ms] resize-none" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800 text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-foreground-primary hover:bg-primary-hover hover:-translate-y-0.5 hover:shadow-medium active:translate-y-0 transition-all duration-[80ms] ease-[cubic-bezier(0.34,1.56,0.64,1)]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending Message...
            </> : "Send Message"}
        </Button>
      </div>
    </Form>;
}